import regions from "@/data/locale-regions.json";
import { BLOG_LOCALES } from "@/lib/blog/locales";
import { isSiteLocale } from "./config";
import { persistSiteLocalePreference, CC_LOCALE_COOKIE_MAX_AGE_SECONDS } from "./locale-preference";
import type { Lang } from "@/lib/types";

export const CC_LANGUAGE_CHOICE_COOKIE = "cc-language-choice";
export const CC_LANGUAGE_CHOICE_EVENT = "cc-language-choice-change";

export { normalizeVisitorCountry } from "../visitor-country";

export function preferredBrowserLocales(languages: readonly string[]): Lang[] {
  return [...new Set(languages.map((language) => language.toLowerCase().split(/[-_]/)[0]).map((language) => language === "nb" || language === "nn" ? "no" : language))]
    .filter((language): language is Lang => BLOG_LOCALES.includes(language as Lang));
}

export function suggestVisitorLocale(country: string | null, languages: readonly string[], available: readonly Lang[]): Lang | null {
  const preferred = preferredBrowserLocales(languages);
  const region = country ? regions.regions[country as keyof typeof regions.regions] : undefined;
  if (region) {
    const local = region.locales.filter((locale): locale is Lang => available.includes(locale as Lang));
    return preferred.find((locale) => local.includes(locale)) ?? local[0] ?? null;
  }
  return preferred.find((locale) => available.includes(locale)) ?? null;
}

export function languageFlag(locale: Lang): string {
  return Array.from(regions.flags[locale], (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0))).join("");
}

export function readLanguageChoice(): Lang | null {
  if (typeof document === "undefined") return null;
  const value = document.cookie.split(";").map((cookie) => cookie.trim()).find((cookie) => cookie.startsWith(`${CC_LANGUAGE_CHOICE_COOKIE}=`))?.slice(CC_LANGUAGE_CHOICE_COOKIE.length + 1);
  return BLOG_LOCALES.includes(value as Lang) ? value as Lang : null;
}

/** An explicit choice is separate from the automatically updated page-locale cookie. */
export function persistLanguageChoice(locale: Lang): void {
  if (typeof document === "undefined") return;
  if (isSiteLocale(locale)) persistSiteLocalePreference(locale);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CC_LANGUAGE_CHOICE_COOKIE}=${locale}; Path=/; Max-Age=${CC_LOCALE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  window.dispatchEvent(new Event(CC_LANGUAGE_CHOICE_EVENT));
}
